import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from 'axios';
import PayPalButton from './HelcimPayment';

const Payment = ({
  subtotal,
  shippingMethod,
  handleShippingChange,
  deliveryTime,
  setDeliveryTime,
  deliveryTimeOptions,
  timeStatus,
  total,
  handleOrderSubmit,
  isFormValid,
  isSubmitting,
  cartItems,
  setIsProcessingPayPal // Add this prop
}) => {
  const [paymentMethod, setPaymentMethod] = useState('PayPal');
  const [paymentError, setPaymentError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  const [loadingPoints, setLoadingPoints] = useState(true);
  const [showPayPalButtons, setShowPayPalButtons] = useState(false);

  // Fetch user points balance
  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const token = localStorage.getItem('AficFreshtoken');
        if (!token) {
          setLoadingPoints(false);
          return;
        }
        
        const response = await axios.get('https://afric-fresh-backend.onrender.com/user/points', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setPointsBalance(response.data.points || 0);
        setLoadingPoints(false);
      } catch (error) {
        console.error('Error fetching points:', error);
        setLoadingPoints(false);
      }
    };

    fetchPoints();
  }, []);

  // Calculate points discount when usePoints changes
  useEffect(() => {
    if (usePoints && pointsBalance > 0) {
      const maxDiscount = Math.floor(pointsBalance / 100);
      const discount = Math.min(maxDiscount, subtotal);
      setPointsDiscount(discount);
    } else {
      setPointsDiscount(0);
    }
  }, [usePoints, pointsBalance, subtotal]);

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
    setShowPayPalButtons(false);
    setPaymentError('');
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    console.log('Payment succeeded:', paymentResponse);
    setIsProcessingPayPal(true);
    
    // Format PayPal response
    const formattedResponse = {
      id: paymentResponse.id,
      status: paymentResponse.status,
      amount: paymentResponse.purchase_units[0].amount.value,
      currency: paymentResponse.purchase_units[0].amount.currency_code,
      create_time: paymentResponse.create_time,
      payer: paymentResponse.payer,
      paymentSource: paymentResponse.payment_source,
      paymentMethod: 'PayPal'
    };
  
    try {
      const token = localStorage.getItem('AficFreshtoken');
      
      // Send payment confirmation email
      await axios.post(
        'https://afric-fresh-backend.onrender.com/order/send-payment-confirmation',
        {
          paymentId: formattedResponse.id,
          amount: formattedResponse.amount,
          currency: formattedResponse.currency,
          email: formattedResponse.payer?.email_address,
          status: formattedResponse.status,
          cartItems, // Include cart items if needed for the email
          total: finalTotal // Include the final total
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      // Submit the full order with payment details
      await handleOrderSubmit({
        ...formattedResponse,
        pointsUsed: usePoints ? pointsDiscount * 100 : 0,
        pointsDiscount,
        shippingMethod,
        deliveryTime,
        finalTotal
      });
      
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentError(error.response?.data?.message || 'Payment was successful but order processing failed. Please contact support.');
      setIsProcessingPayPal(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setPaymentError(error.message || 'Payment failed. Please try again.');
    setIsProcessingPayPal(false);
    setShowPayPalButtons(false);
  };

  const handlePlaceOrder = () => {
    if (!isFormValid() || isSubmitting) return;

    if (paymentMethod === 'PayPal') {
      setShowPayPalButtons(true);
    } 
  };

  const calculateTotal = () => {
    const shippingCost = shippingMethod === 'Standard delivery' ? 3.5 : 9;
    return (subtotal - pointsDiscount) + shippingCost;
  };

  const finalTotal = calculateTotal();

  return (
    <div className="bg-white p-6 rounded-lg shadow-md order-summary max-w-md lg:sticky lg:top-20">
      <h2 className="text-2xl font-semibold mb-6">Your Order</h2>

      <div className="border-b pb-4 mb-4">
        <div className="flex justify-between mb-2">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>

        {/* Points Balance and Redemption Section */}
        {!loadingPoints && pointsBalance > 0 && (
          <div className="mb-4 border-t pt-4">
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="font-medium">Points Balance: </span>
                <span>{pointsBalance} points (${(pointsBalance / 100).toFixed(2)})</span>
              </div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={usePoints}
                  onChange={(e) => setUsePoints(e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm">Use points</span>
              </label>
            </div>
            
            {usePoints && (
              <div className="bg-green-50 p-3 rounded-md text-sm">
                <div className="flex justify-between mb-1">
                  <span>Points applied:</span>
                  <span>-${pointsDiscount.toFixed(2)}</span>
                </div>
                <div className="text-xs text-green-700">
                  Using {pointsDiscount * 100} points (100 points = $1)
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <h3 className="font-medium mb-2">Shipping Method</h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="Flat rate"
                checked={shippingMethod === 'Flat rate'}
                onChange={handleShippingChange}
                className="mr-2"
              />
              <span>Flat rate: $9.00</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="Standard delivery"
                checked={shippingMethod === 'Standard delivery'}
                onChange={handleShippingChange}
                className="mr-2"
              />
              <span>Standard delivery: $3.50</span>
            </label>
          </div>
        </div>

        {shippingMethod === 'Standard delivery' && (
          <div className="mb-4">
            <h3 className="font-medium mb-2">Delivery Time</h3>
            <div className="space-y-2">
              {deliveryTimeOptions.map((time) => (
                <label key={time} className="flex items-center">
                  <input
                    type="radio"
                    value={time}
                    checked={deliveryTime === time}
                    onChange={() => setDeliveryTime(time)}
                    disabled={!!timeStatus[time]}
                    className="mr-2"
                  />
                  <span>{time}</span>
                  {timeStatus[time] && (
                    <span className="text-red-500 text-sm ml-2">{timeStatus[time]}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border-b pb-4 mb-4">
        <h3 className="font-medium mb-2">Payment Method</h3>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              value="PayPal"
              checked={paymentMethod === 'PayPal'}
              onChange={handlePaymentMethodChange}
              className="mr-2"
            />
            <span>PayPal</span>
          </label>

        </div>


        {paymentMethod === 'PayPal' && showPayPalButtons && (
          <div className="mt-4">
           <PayPalButton
            amount={finalTotal} 
            currency="USD"
            cartItems={cartItems}
            shippingMethod={shippingMethod}
            subtotal={subtotal}
            onPaymentSuccess={handlePaymentSuccess} // Pass the success handler
            onPaymentError={handlePaymentError} // Pass the error handler 
            handleOrderSubmit={handleOrderSubmit}
            setIsProcessingPayPal={setIsProcessingPayPal}
          />
          </div>
        )}
      </div>

      <div className="border-b pb-4 mb-4">
        <div className="flex justify-between font-bold">
          <span>Total</span>
          <span>${finalTotal.toFixed(2)} USD</span>
        </div>
      </div>

      {paymentMethod !== 'PayPal' || !showPayPalButtons ? (
        <button
          onClick={handlePlaceOrder}
          disabled={!isFormValid() || isSubmitting}
          className={`w-full py-3 px-4 rounded font-medium flex items-center justify-center ${
            isFormValid() && !isSubmitting
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing order...
            </>
          ) : (
            paymentMethod === 'PayPal' ? 'Proceed to PayPal' : 'Place Order'
          )}
        </button>
      ) : null}

      {paymentError && (
        <div className="mt-4 p-3 bg-red-50 text-red-500 rounded-md text-sm">
          {paymentError}
        </div>
      )}
    </div>
  );
};

export default Payment;